var _a, _b;
import { Resource } from "@rest-hooks/rest";
import { DployContextStore } from "@ploy-lib/core";
import qs from "qs";
import { deeplyApplyKeyTransform, deeplyApplyValueTransform, alphabeticalSort } from "@ploy-lib/core";
import camelCase from "lodash/camelCase";
import isTypedArray from "lodash/isTypedArray";
const paramRegex = /{(\w+)}/g;
class MissingParameterError extends Error {
    constructor(templates, missingParameters) {
        const message = `The parameters does not satisfy any of the url templates:
	${templates.map(x => `- ${x}`).join("\n")}

Add some or all of the missing parameters to satisfy one of the templates:
	${missingParameters.map(x => `- ${x}`).join("\n")}`;
        super(message);
        this.name = "MissingParameterError";
        this.templates = templates;
        this.missingParameters = missingParameters;
    }
}
export function paramsToString(searchParams) {
    return qs.stringify(searchParams, {
        arrayFormat: "repeat",
        sort: alphabeticalSort,
        addQueryPrefix: true
    });
}
function getBody(data) {
    if (data == null)
        return data;
    // Possible types that are accepted as body
    if (typeof Blob !== "undefined" && data instanceof Blob)
        return data;
    if (typeof FormData !== "undefined" && data instanceof FormData)
        return data;
    if (typeof URLSearchParams !== "undefined" && data instanceof URLSearchParams)
        return data;
    if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer)
        return data;
    if (isTypedArray(data))
        return data;
    if (typeof ReadableStream !== "undefined" && data instanceof ReadableStream)
        return data;
    if (typeof data === "string")
        return data;
    // Otherwise create a JSON blob
    return new Blob([JSON.stringify(data)], {
        type: "application/json"
    });
}
class BaseResource extends (_b = Resource) {
    static async fetch(input, init) {
        return this.fetchResponse(input, init).then((response) => {
            if (!response.headers.get("content-type")?.includes("json") ||
                response.status === 204)
                return response.text();
            DployContextStore.context ??= {};
            response.headers.forEach((val, key) => {
                if (key.toLowerCase().startsWith("dploycontext-"))
                    DployContextStore.context[key] = val;
            });
            return response.json().catch(error => {
                error.status = 400;
                throw error;
            });
        });
    }
    /** Get the url for many SimpleResources
     *
     * Default implementation conforms to common REST patterns
     */
    static listUrl(allParams = {}) {
        if (Object.prototype.hasOwnProperty.call(allParams, "url") &&
            allParams.url &&
            typeof allParams.url === "string") {
            return allParams.url;
        }
        const [urlRoot, searchParams] = this.interpolatedUrlRoot(allParams);
        return `${urlRoot}${paramsToString(searchParams)}`;
    }
    /** Get the url for a SimpleResource
     *
     * Default implementation conforms to common REST patterns
     */
    static url(urlParams) {
        if (Object.prototype.hasOwnProperty.call(urlParams, "url") &&
            urlParams.url &&
            typeof urlParams.url === "string") {
            return urlParams.url;
        }
        const [urlRoot, params] = this.interpolatedUrlRoot(urlParams);
        if (this.pk(params) !== undefined) {
            if (urlRoot.endsWith("/")) {
                return `${urlRoot}${this.pk(params)}`;
            }
            return `${urlRoot}/${this.pk(params)}`;
        }
        return urlRoot;
    }
    /** Interpolate the url template of a BaseResource */
    static interpolatedUrlRoot(params) {
        let allMissingParams = [];
        const templates = this.urlTemplates
            ? [...this.urlTemplates, this.urlRoot]
            : [this.urlRoot];
        for (const urlTemplate of templates) {
            let url = urlTemplate;
            let unusedParams = { ...params };
            let missingParams = [];
            let match; // Replace with matchAll when it works
            while ((match = paramRegex.exec(urlTemplate))) {
                let param = match[1];
                if (!params ||
                    !Object.prototype.hasOwnProperty.call(params, param) ||
                    params[param] == null) {
                    missingParams.push(param);
                    continue;
                }
                url = url.replace(`{${param}}`, params[param]);
                delete unusedParams[param];
            }
            if (missingParams.length > 0)
                allMissingParams.push(...missingParams);
            else
                return [url, unusedParams];
        }
        throw new MissingParameterError(templates, allMissingParams);
    }
}
_a = BaseResource;
BaseResource.getFetchInit = (options) => {
    return Reflect.get(_b, "getFetchInit", _a).call(_a, {
        ...options,
        credentials: "same-origin",
        headers: {
            ...options.headers,
            ...(DployContextStore.context ?? {}),
            accept: "application/json"
        },
        body: getBody(options.body)
    });
};
BaseResource.calcPathToFields = "";
BaseResource.calcPathToValue = "";
export { BaseResource };
export class BaseCamelCasedResource extends BaseResource {
    static async fetch(input, init) {
        const jsonResponse = await super.fetch(input, init);
        const data = deeplyApplyKeyTransform(jsonResponse, camelCase);
        const dataWithoutNull = deeplyApplyValueTransform(data, v => v == null ? undefined : v);
        return dataWithoutNull;
    }
}
//# sourceMappingURL=BaseResource.js.map